
import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Loader2, AlertCircle, Map as MapIcon, Route, AlertTriangle, CheckCircle2, Activity, Flame, MessageSquare } from 'lucide-react';
import { MapContainer, TileLayer, Marker, useMap, Polyline, Pane } from 'react-leaflet';
import L from 'leaflet';
import { fetchDeletionSuggestions, resolveDeletionSuggestion, fetchAllRoutes, deleteRoute, updateGpxRoute, fetchGpxWithProxy, fetchPendingRoutes, fetchCategorySuggestions, resolveCategorySuggestion, fetchSystemConfig, saveSystemConfig, updateNordicConfig, fetchTesterRequests, resolveTesterRequest, getDefaultAiPrompt, fetchApprovedTesters, fetchAllUsers, endTesterProfile, promoteUserToTester, subscribeToTesterThreadsForAdmin, fetchSeoMetricsReport, runExternalSpotsSync, subscribeToExternalSyncStatus, functions } from '../services/firebaseService';
import { httpsCallable } from 'firebase/functions';
import { DeletionSuggestion, CategorySuggestion, Coordinates, GpxRoute, RouteDifficulty, RouteRegion, TesterRequest, TesterProfile, AppUser, TesterThread, MapBounds } from '../types';
import { MAP_LAYERS, ROUTE_COLOR_PALETTE } from '../constants';
import { ForceMapResize } from './map/MapUtils';
import TesterInboxModal from './TesterInboxModal';
import { getAdminTabMeta, AdminTab } from './admin/adminNavigation';
import AdminDesktopSidebar from './admin/AdminDesktopSidebar';
import AdminDesktopContextBar from './admin/AdminDesktopContextBar';
import AdminTopTabs from './admin/AdminTopTabs';
import AdminAiConfigPanel from './admin/panels/AdminAiConfigPanel';
import AdminPrivacyPanel from './admin/panels/AdminPrivacyPanel';
import AdminTestersPanel from './admin/panels/AdminTestersPanel';
import AdminToolsPanel from './admin/panels/AdminToolsPanel';
import AdminSpotsPanel from './admin/panels/AdminSpotsPanel';
import AdminSuggestionsPanel from './admin/panels/AdminSuggestionsPanel';
import AdminRoutesPanel from './admin/panels/AdminRoutesPanel';
import AdminRouteCard from './admin/panels/AdminRouteCard';
import AdminMessagesPanel from './admin/panels/AdminMessagesPanel';
import AdminCampaignsPanel from './admin/panels/AdminCampaignsPanel';
import AdminArticlesPanel from './admin/panels/AdminArticlesPanel';
import AiUsagePanel from './admin/panels/AiUsagePanel';
import AdminCreatorsPanel from './admin/panels/AdminCreatorsPanel';
import AdminPrescanDashboard from './admin/panels/AdminPrescanDashboard';
import AdminImageScanDashboard from './admin/panels/AdminImageScanDashboard';
import AdminGoogleScanPanel from './admin/panels/AdminGoogleScanPanel';
import AdminErrorReportsPanel from './admin/panels/AdminErrorReportsPanel';
import { useToast } from '../contexts/ToastContext';
// AdminPremiumPanel functionality moved to AdminRevenueDashboard

interface AdminPanelProps {
  initialTab?: 'spots' | 'suggestions' | 'routes' | 'pending' | 'tools' | 'ai' | 'testers';
  inboxOnly?: boolean;
  currentUser?: AppUser | null;
  onClose: () => void;
  onViewSpot: (spotId: string, location: Coordinates) => void;
  onSpotRemoved?: (id: string) => void;
  onOpenRouteUpload?: () => void;
  onClearArea?: () => Promise<void>;
  onReevaluateArea?: () => Promise<any>;
  currentViewBounds?: MapBounds | null;
}

const DIFFICULTY_ICONS: Record<RouteDifficulty, React.ReactNode> = {
  'Easy': <CheckCircle2 size={12} />,
  'Medium': <Activity size={12} />,
  'Hard': <AlertTriangle size={12} />,
  'Extreme': <Flame size={12} />
};

const determineRegion = (lat: number): RouteRegion => {
  if (lat >= 65.0) return 'north';
  if (lat >= 62.0) return 'central';
  return 'south';
};

const PreviewMapController: React.FC<{ center?: [number, number], bounds?: L.LatLngBoundsExpression }> = ({ center, bounds }) => {
  const map = useMap();
  useEffect(() => {
    const runInvalidate = () => {
      map.invalidateSize();
      if (bounds) {
        map.fitBounds(bounds, { padding: [20, 20], animate: false });
      } else if (center) {
        map.setView(center, 14, { animate: false });
      }
    };
    runInvalidate();
    const timers = [
      setTimeout(runInvalidate, 100),
      setTimeout(runInvalidate, 500)
    ];
    return () => timers.forEach(t => clearTimeout(t));
  }, [map, center, bounds]);
  return null;
};

const AdminPanel: React.FC<AdminPanelProps> = ({ initialTab = 'spots', inboxOnly = false, currentUser, onClose, onViewSpot, onSpotRemoved, onOpenRouteUpload, onClearArea, onReevaluateArea, currentViewBounds }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [activeTab, setActiveTab] = useState<AdminTab>(inboxOnly ? 'testers' : initialTab);
  const [deleteSuggestions, setDeleteSuggestions] = useState<DeletionSuggestion[]>([]);
  const [categorySuggestions, setCategorySuggestions] = useState<CategorySuggestion[]>([]);
  const [routes, setRoutes] = useState<GpxRoute[]>([]);
  const [pendingRoutes, setPendingRoutes] = useState<GpxRoute[]>([]);
  const [routeStatusFilter, setRouteStatusFilter] = useState<'all' | 'public' | 'private'>('all');
  const [testerRequests, setTesterRequests] = useState<TesterRequest[]>([]);
  const [approvedTesters, setApprovedTesters] = useState<TesterProfile[]>([]);
  const [routeGeometries, setRouteGeometries] = useState<Record<string, [number, number][]>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [isClearingArea, setIsClearingArea] = useState(false);
  const [confirmClearArea, setConfirmClearArea] = useState(false);

  // AI Config State
  const [aiPrompt, setAiPrompt] = useState('');
  const [isAiSaved, setIsAiSaved] = useState(false);
  const [aiModel, setAiModel] = useState('');
  const [availableModels, setAvailableModels] = useState<{ id: string; displayName: string; inputTokenLimit: number | null; outputTokenLimit: number | null }[]>([]);
  const [isModelSaved, setIsModelSaved] = useState(false);
  const [imageModel, setImageModel] = useState('imagen-3.0-generate-002');
  const [isImageModelSaved, setIsImageModelSaved] = useState(false);
  const [articlePrompt, setArticlePrompt] = useState('');
  const [isArticlePromptSaved, setIsArticlePromptSaved] = useState(false);
  const [youtubeKeyStatus, setYoutubeKeyStatus] = useState<{ hasYouTubeKey: boolean; source: 'firestore' | 'env' | 'none'; hasSvvKey?: boolean; svvSource?: 'firestore' | 'env' | 'none' } | null>(null);
  const [isYouTubeKeySaved, setIsYouTubeKeySaved] = useState(false);
  const [isSvvKeySaved, setIsSvvKeySaved] = useState(false);
  const [isMapillaryTokenSaved, setIsMapillaryTokenSaved] = useState(false);
  const [isYrKeySaved, setIsYrKeySaved] = useState(false);
  const [isTrafikverketKeySaved, setIsTrafikverketKeySaved] = useState(false);

  // System Config State
  const [testerEnabled, setTesterEnabled] = useState(true);
  const [adminReevaluateButtonEnabled, setAdminReevaluateButtonEnabled] = useState(true);
  const [adminSubscriptionPushEnabled, setAdminSubscriptionPushEnabled] = useState(true);
  const [isConfigLoaded, setIsConfigLoaded] = useState(false);
  const [privacyContent, setPrivacyContent] = useState({ no: '', en: '' });
  const [isPrivacySaved, setIsPrivacySaved] = useState(false);
  const [termsContent, setTermsContent] = useState({ no: '', en: '' });
  const [isTermsSaved, setIsTermsSaved] = useState(false);
  const [inboxTester, setInboxTester] = useState<TesterProfile | null>(null);
  const [inboxUser, setInboxUser] = useState<{ uid: string; displayName?: string | null; email?: string | null; photoURL?: string | null } | null>(null);
  const [allUsers, setAllUsers] = useState<AppUser[]>([]);
  const [testerThreads, setTesterThreads] = useState<TesterThread[]>([]);
  const [processingId, setProcessingId] = useState<string | null>(null);

  // Nordic expansion config
  const [nordicCountryConfig, setNordicCountryConfig] = useState<Record<string, boolean>>({
    sweden: false, denmark: false, finland: false, iceland: false
  });

  // TestFlight sync state
  const [testflightSyncEnabled, setTestflightSyncEnabled] = useState(false);
  const [testflightBetaGroupId, setTestflightBetaGroupId] = useState('');
  const [testflightPublic, setTestflightPublic] = useState(false);
  const [testflightSyncStatus, setTestflightSyncStatus] = useState<{ last_sync_at?: any; last_sync_result?: { added: number; removed: number; errors: number; duration_ms: number } } | null>(null);


  const DEFAULT_PRIVACY_NO = `# Personvernserklæring for Fricamp

**Sist oppdatert: 22. februar 2026**

Takle Consulting ("vi", "oss", "vår") driver Fricamp-applikasjonen. Denne personvernserklæringen informerer deg om våre retningslinjer for innsamling, bruk og deling av personopplysninger når du bruker vår tjeneste.

## 1. Informasjon vi samler inn
Vi samler inn flere ulike typer informasjon for ulike formål for å tilby og forbedre vår tjeneste til deg.

*   **Brukerprofil:** E-postadresse, visningsnavn og profilbilde.
*   **Innstillinger:** Kjøretøytype (standard/offroad), filterinnstillinger og elbil-status.
*   **Aktivitetsdata:**
    *   **Besøkslogg:** Steder du har markert som besøkt.
    *   **Anmeldelser:** Tekst, terningkast, bilder og videoer du laster opp.
    *   **Sporing (Track Recording):** GPS-koordinater, distanse og høydeprofil ved bruk av turopptaker.
    *   **Posisjonsdeling:** Sanntids-posisjon (kun hvis aktivert av deg).
*   **Samarbeid:** Deltakelse i samarbeidskart og invitasjoner.

## 2. Hvordan vi bruker informasjonen
*   For å levere og vedlikeholde tjenesten samt skreddersy kartopplevelsen.
*   For å håndtere medlemskap og tester-dialog.
*   For å forbedre datakvaliteten i kartet gjennom dine bidrag.
*   For å varsle om relevante hendelser (hvis varslinger er aktivert).

## 3. Tredjepartstjenester
Vi benytter anerkjente underleverandører for sikker drift:
*   **Google Firebase:** Database, autentisering og lagring av media. Data lagres sikkert i skyen.
*   **Stripe:** Behandling av betaling og abonnement. Vi lagrer aldri kortopplysninger selv.
*   **Karttjenester:** Google Maps og Leaflet for visning av kartlag.

## 4. Dine rettigheter
Du har full kontroll over dine data:
*   Du kan når som helst be om innsyn eller rettelse.
*   **Sletting:** Du kan slette hele din konto og alle tilhørende data umiddelbart via "Konto"-fanen i profilen.
*   Du kan administrere samtykker for posisjon og varslinger i telefonens innstillinger eller i appen.

## 5. Kontakt
For spørsmål om personvern, kontakt Takle Consulting via tilbakemeldingsskjemaet i appen.`;

  const DEFAULT_PRIVACY_EN = `# Privacy Policy for Fricamp

**Last updated: February 22, 2026**

Takle Consulting ("we", "us", "our") operates the Fricamp application. This privacy policy informs you of our policies regarding the collection, use, and disclosure of personal data when you use our service.

## 1. Information Collection and Use
We collect several different types of information for various purposes to provide and improve our service to you.

*   **User Profile:** Email address, display name, and profile picture.
*   **Settings:** Vehicle type (standard/offroad), filter settings, and EV status.
*   **Activity Data:**
    *   **Visit Log:** Locations you have marked as visited.
    *   **Reviews:** Text, ratings, photos, and videos you upload.
    *   **Tracking:** GPS coordinates, distance, and elevation profile when using the track recorder.
    *   **Location Sharing:** Real-time position (only if enabled by you).
*   **Collaboration:** Participation in collaborative maps and invitations.

## 2. How We Use Data
*   To provide and maintain the service and customize the map experience.
*   To manage memberships and tester communications.
*   To improve map data quality through your contributions.
*   To notify you about relevant events (if notifications are enabled).

## 3. Third-Party Services
We use reputable subprocessors for secure operation:
*   **Google Firebase:** Database, authentication, and media storage. Data is stored securely in the cloud.
*   **Stripe:** Processing of payments and subscriptions. We never store card details ourselves.
*   **Map Services:** Google Maps and Leaflet for displaying map layers.

## 4. Your Rights
You have full control over your data:
*   To provide and maintain the service and customize the map experience.
*   You can request access or correction at any time.
*   **Deletion:** You can delete your entire account and all associated data immediately via the "Account" tab in your profile.
*   You can manage consents for location and notifications in your phone settings or within the app.

## 5. Contact
For privacy-related questions, contact Takle Consulting via the feedback form in the app.`;

  const DEFAULT_TERMS_NO = `# Brukervilkår for Fricamp

**Sist oppdatert: 4. mars 2026**

Disse brukervilkårene regulerer din bruk av Fricamp-applikasjonen ("Tjenesten") levert av Takle Consulting ("vi", "oss", "vår").

## 1. Aksept av vilkår
Ved å bruke Tjenesten godtar du disse vilkårene. Hvis du ikke godtar vilkårene, skal du ikke bruke Tjenesten.

## 2. Bruk av tjenesten
Du kan bruke Tjenesten til å finne, dele og vurdere frilufts- og campingplasser i Norge. Du forplikter deg til å bruke Tjenesten i samsvar med gjeldende lover og regler.

## 3. Brukergenerert innhold
Innhold du deler (anmeldelser, bilder, ruter) forblir ditt, men du gir oss en ikke-eksklusiv rett til å vise det i Tjenesten. Vi kan fjerne innhold som bryter disse vilkårene.

## 4. Ansvarsbegrensning
Tjenesten leveres "som den er". Vi garanterer ikke nøyaktigheten av kartdata eller brukeranmeldelser. Bruk alltid egen dømmekraft ved valg av oppholdssted.

## 5. Endringer
Vi kan oppdatere disse vilkårene. Fortsatt bruk etter endringer betyr aksept av oppdaterte vilkår.

## 6. Kontakt
For spørsmål om vilkårene, kontakt Takle Consulting via tilbakemeldingsskjemaet i appen.`;

  const DEFAULT_TERMS_EN = `# Terms of Service for Fricamp

**Last updated: March 4, 2026**

These terms of service govern your use of the Fricamp application ("Service") provided by Takle Consulting ("we", "us", "our").

## 1. Acceptance of Terms
By using the Service, you agree to these terms. If you do not agree, you should not use the Service.

## 2. Use of the Service
You may use the Service to find, share, and review outdoor and camping spots in Norway. You agree to use the Service in compliance with applicable laws and regulations.

## 3. User-Generated Content
Content you share (reviews, photos, routes) remains yours, but you grant us a non-exclusive right to display it within the Service. We may remove content that violates these terms.

## 4. Limitation of Liability
The Service is provided "as is". We do not guarantee the accuracy of map data or user reviews. Always use your own judgement when choosing a campsite.

## 5. Changes
We may update these terms. Continued use after changes constitutes acceptance of the updated terms.

## 6. Contact
For questions about these terms, contact Takle Consulting via the feedback form in the app.`;

  // Bekreftelses-state
  const [confirmingSpotAction, setConfirmingSpotAction] = useState<{ id: string, action: 'accept' | 'reject' } | null>(null);
  const [deletingRouteId, setDeletingRouteId] = useState<string | null>(null);
  const [editingRouteId, setEditingRouteId] = useState<string | null>(null);
  const [reviewingRouteId, setReviewingRouteId] = useState<string | null>(null);
  const [isMobileViewport, setIsMobileViewport] = useState(window.innerWidth < 768);
  const [feedCopied, setFeedCopied] = useState(false);
  const [sitemapCopied, setSitemapCopied] = useState(false);
  const [seoReport, setSeoReport] = useState<{
    month: string;
    totals: { views: number; humanViews: number; botViews: number };
    topPages: Array<{ page: string; views: number; humanViews: number; botViews: number }>;
    pageCount: number;
    sitemapUrl: string;
  } | null>(null);
  const [seoReportLoading, setSeoReportLoading] = useState(false);
  const [externalSyncEnabled, setExternalSyncEnabled] = useState(false);
  const [externalSyncStatus, setExternalSyncStatus] = useState<any | null>(null);
  const [externalSyncSources, setExternalSyncSources] = useState<{ nvdb: boolean; osmAmenities: boolean; osmCamp: boolean }>({
    nvdb: true,
    osmAmenities: true,
    osmCamp: true
  });
  const [externalSyncRegions, setExternalSyncRegions] = useState<Array<{
    id: string;
    name: string;
    enabled: boolean;
    bounds: { latMin: number; lonMin: number; latMax: number; lonMax: number };
  }>>([]);
  const [showToolsIntro, setShowToolsIntro] = useState(false);
  const [showSystemSettingsHelp, setShowSystemSettingsHelp] = useState(false);
  const [showExternalSyncHelp, setShowExternalSyncHelp] = useState(false);
  const [showOwnInbox, setShowOwnInbox] = useState(false);
  const [regionSearchQuery, setRegionSearchQuery] = useState('');
  const [regionSearchLoading, setRegionSearchLoading] = useState(false);
  const [regionSearchResults, setRegionSearchResults] = useState<Array<{
    display_name: string;
    boundingbox?: string[];
    lat?: string;
    lon?: string;
  }>>([]);

  const externalSyncRegionValidation = useMemo(() => {
    const issues: string[] = [];
    const invalidIndexes = new Set<number>();
    const seenIds = new Set<string>();

    externalSyncRegions.forEach((r, idx) => {
      const id = String(r.id || '').trim();
      const name = String(r.name || '').trim();
      const { latMin, lonMin, latMax, lonMax } = r.bounds;

      const numbersOk = [latMin, lonMin, latMax, lonMax].every((n) => Number.isFinite(Number(n)));
      const latRangeOk = Number(latMin) >= -90 && Number(latMin) <= 90 && Number(latMax) >= -90 && Number(latMax) <= 90;
      const lonRangeOk = Number(lonMin) >= -180 && Number(lonMin) <= 180 && Number(lonMax) >= -180 && Number(lonMax) <= 180;
      const minLessThanMax = Number(latMin) < Number(latMax) && Number(lonMin) < Number(lonMax);

      if (!id) {
        issues.push(t('admin.validation.missing_id', { idx: idx + 1 }));
        invalidIndexes.add(idx);
      } else if (seenIds.has(id)) {
        issues.push(t('admin.validation.duplicate_id', { id }));
        invalidIndexes.add(idx);
      } else {
        seenIds.add(id);
      }

      if (!name) {
        issues.push(t('admin.validation.missing_name', { id: id || `#${idx + 1}` }));
        invalidIndexes.add(idx);
      }

      if (!numbersOk) {
        issues.push(t('admin.validation.bbox_not_numbers', { id: id || `#${idx + 1}` }));
        invalidIndexes.add(idx);
      } else {
        if (!latRangeOk) {
          issues.push(t('admin.validation.lat_range', { id: id || `#${idx + 1}` }));
          invalidIndexes.add(idx);
        }
        if (!lonRangeOk) {
          issues.push(t('admin.validation.lon_range', { id: id || `#${idx + 1}` }));
          invalidIndexes.add(idx);
        }
        if (!minLessThanMax) {
          issues.push(t('admin.validation.min_max', { id: id || `#${idx + 1}` }));
          invalidIndexes.add(idx);
        }
      }
    });

    return {
      isValid: issues.length === 0,
      issues,
      invalidIndexes
    };
  }, [externalSyncRegions]);

  // Default regions to collapsed (empty Set)
  const [expandedRegions, setExpandedRegions] = useState<Set<RouteRegion>>(new Set());

  const [editFields, setEditFields] = useState<{ name: string; description: string; difficulty: RouteDifficulty }>({ name: '', description: '', difficulty: 'Medium' });
  const getThreadSortValue = (createdAt: any): number => {
    if (!createdAt) return 0;
    if (createdAt?.toMillis && typeof createdAt.toMillis === 'function') return createdAt.toMillis();
    if (typeof createdAt?.seconds === 'number') return createdAt.seconds * 1000;
    return 0;
  };

  useEffect(() => {
    const onResize = () => setIsMobileViewport(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (inboxOnly) setActiveTab('testers');
  }, [inboxOnly]);

  useEffect(() => {
    if (activeTab === 'tools') {
      setIsLoading(false);
      loadSeoReport();
      const key = `fricamp_admin_tools_intro_dismissed_${currentUser?.uid || 'anon'}`;
      const dismissed = window.localStorage.getItem(key) === '1';
      setShowToolsIntro(!dismissed);
      // loadData() kalles etter if-blokken
    }
    loadData();
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== 'tools') return;
    const q = regionSearchQuery.trim();
    if (q.length < 3) {
      setRegionSearchResults([]);
      setRegionSearchLoading(false);
      return;
    }

    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setRegionSearchLoading(true);
      try {
        const url = `https://nominatim.openstreetmap.org/search?format=jsonv2&addressdetails=1&limit=8&countrycodes=no&q=${encodeURIComponent(q)}`;
        const res = await fetch(url, { headers: { Accept: 'application/json', 'Accept-Language': 'no,nb,nn,en' } });
        const data = res.ok ? await res.json() : [];
        if (!cancelled) {
          setRegionSearchResults(Array.isArray(data) ? data : []);
        }
      } catch {
        if (!cancelled) setRegionSearchResults([]);
      } finally {
        if (!cancelled) setRegionSearchLoading(false);
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [activeTab, regionSearchQuery]);

  useEffect(() => {
    const unsub = subscribeToTesterThreadsForAdmin((threads) => {
      setTesterThreads(threads);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (activeTab !== 'tools') return;
    const unsub = subscribeToExternalSyncStatus((status) => {
      setExternalSyncStatus(status);
    });
    return () => unsub();
  }, [activeTab]);

  const loadData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      if (activeTab === 'spots') {
        const data = await fetchDeletionSuggestions();
        setDeleteSuggestions(data);
      } else if (activeTab === 'suggestions') {
        const data = await fetchCategorySuggestions();
        setCategorySuggestions(data);
      } else if (activeTab === 'pending') {
        const data = await fetchPendingRoutes();
        setPendingRoutes(data);
        loadRouteGeometries(data);
      } else if (activeTab === 'routes') {
        const data = await fetchAllRoutes();
        setRoutes(data);
        loadRouteGeometries(data);
      } else if (activeTab === 'testers' || activeTab === 'messages') {
        const [requests, testers, users, config, testflightConfig] = await Promise.all([
          fetchTesterRequests(),
          fetchApprovedTesters(),
          fetchAllUsers(500),
          fetchSystemConfig('config'),
          fetchSystemConfig('testflight_sync'),
        ]);
        setTesterRequests(requests);
        setApprovedTesters(testers);
        setAllUsers(users);
        if (config) {
          setTesterEnabled(config.tester_applications_enabled !== false);
        }
        if (testflightConfig) {
          setTestflightSyncEnabled(testflightConfig.enabled === true);
          setTestflightBetaGroupId(testflightConfig.beta_group_id || '');
          setTestflightPublic(testflightConfig.public === true);
          setTestflightSyncStatus({
            last_sync_at: testflightConfig.last_sync_at || null,
            last_sync_result: testflightConfig.last_sync_result || null
          });
        }
      } else if (activeTab === 'ai') {
        const config = await fetchSystemConfig('ai_config');
        setAiPrompt(config?.prompt || getDefaultAiPrompt());
        setAiModel(config?.model || '');
        setImageModel(config?.image_model || 'imagen-3.0-generate-002');
        setArticlePrompt(config?.article_prompt || '');
        // Fetch YouTube key status (secure — only returns boolean, never the key)
        try {
          const getStatus = httpsCallable(functions, 'getAiSecretsStatus');
          const statusRes = await getStatus();
          setYoutubeKeyStatus(statusRes.data as any);
        } catch (e) {
          console.warn('Could not fetch YouTube key status');
        }
        // Fetch available models from Google API
        try {
          const listModels = httpsCallable(functions, 'listAiModels');
          const res = await listModels();
          const data = res.data as any;
          if (data?.ok && Array.isArray(data.models)) {
            setAvailableModels(data.models);
            if (!config?.model && data.currentModel) setAiModel(data.currentModel);
          }
        } catch (e) {
          console.warn('Could not fetch AI models list:', e);
        }
        const [requests, testers, privacy] = await Promise.all([
          fetchTesterRequests(),
          fetchApprovedTesters(),
          fetchSystemConfig('privacy_policy')
        ]);
        setTesterRequests(requests);
        setApprovedTesters(testers);
        setPrivacyContent({
          no: privacy?.no || DEFAULT_PRIVACY_NO,
          en: privacy?.en || DEFAULT_PRIVACY_EN
        });
      } else if (activeTab === 'privacy') {
        const [privacy, terms] = await Promise.all([
          fetchSystemConfig('privacy_policy'),
          fetchSystemConfig('terms_of_service')
        ]);
        setPrivacyContent({
          no: privacy?.no || DEFAULT_PRIVACY_NO,
          en: privacy?.en || DEFAULT_PRIVACY_EN
        });
        setTermsContent({
          no: terms?.no || DEFAULT_TERMS_NO,
          en: terms?.en || DEFAULT_TERMS_EN
        });
      } else if (activeTab === 'tools') {
        const [config, externalSyncConfig, externalSyncStatusDoc, nordicCfg] = await Promise.all([
          fetchSystemConfig('config'),
          fetchSystemConfig('external_sync'),
          fetchSystemConfig('external_sync_status'),
          fetchSystemConfig('nordic_config')
        ]);
        if (nordicCfg) {
          setNordicCountryConfig({
            sweden: nordicCfg.sweden === true,
            denmark: nordicCfg.denmark === true,
            finland: nordicCfg.finland === true,
            iceland: nordicCfg.iceland === true,
          });
        }
        if (config) {
          setTesterEnabled(config.tester_applications_enabled !== false);
          setAdminReevaluateButtonEnabled(config.show_admin_reevaluate_button !== false);
          setAdminSubscriptionPushEnabled(config.admin_push_new_subscription_enabled !== false);
        }
        setExternalSyncEnabled(externalSyncConfig?.enabled === true);
        setExternalSyncSources({
          nvdb: externalSyncConfig?.sources?.nvdb !== false,
          osmAmenities: externalSyncConfig?.sources?.osmAmenities !== false,
          osmCamp: externalSyncConfig?.sources?.osmCamp !== false
        });
        const loadedRegions = Array.isArray(externalSyncConfig?.regions) ? externalSyncConfig.regions : [];
        setExternalSyncRegions(
          loadedRegions
            .map((r: any) => ({
              id: String(r?.id || ''),
              name: String(r?.name || ''),
              enabled: r?.enabled !== false,
              bounds: {
                latMin: Number(r?.bounds?.latMin) || 0,
                lonMin: Number(r?.bounds?.lonMin) || 0,
                latMax: Number(r?.bounds?.latMax) || 0,
                lonMax: Number(r?.bounds?.lonMax) || 0
              }
            }))
            .filter((r: any) => r.id)
        );
        setExternalSyncStatus(externalSyncStatusDoc || null);
        setIsConfigLoaded(true);
      }
    } catch (err: any) {
      console.error("AdminPanel loadData error:", err);
      setError(t('admin.error_fetch_system', { code: err?.code || 'unknown' }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSavePrivacyPolicy = async () => {
    setProcessingId('privacy-save');
    try {
      await saveSystemConfig('privacy_policy', {
        ...privacyContent,
        updatedAt: new Date().toISOString()
      });
      setIsPrivacySaved(true);
      setTimeout(() => setIsPrivacySaved(false), 2000);
    } catch (e) {
      setError(t('admin.privacy.save_error'));
    } finally {
      setProcessingId(null);
    }
  };

  const handleSaveTerms = async () => {
    setProcessingId('terms-save');
    try {
      await saveSystemConfig('terms_of_service', {
        ...termsContent,
        last_updated: new Date().toISOString()
      });
      setIsTermsSaved(true);
      setTimeout(() => setIsTermsSaved(false), 2000);
    } catch (e) {
      setError('Kunne ikke lagre brukervilkår');
    } finally {
      setProcessingId(null);
    }
  };

  const handleSaveAiConfig = async () => {
    setProcessingId('ai-save');
    try {
      await saveSystemConfig('ai_config', { prompt: aiPrompt, updatedAt: new Date() });
      setIsAiSaved(true);
      setTimeout(() => setIsAiSaved(false), 2000);
    } catch (e) {
      setError(t('admin.error_save_ai'));
    } finally {
      setProcessingId(null);
    }
  };

  const handleSaveArticlePrompt = async () => {
    setProcessingId('article-prompt-save');
    try {
      await saveSystemConfig('ai_config', { article_prompt: articlePrompt });
      setIsArticlePromptSaved(true);
      setTimeout(() => setIsArticlePromptSaved(false), 2000);
    } catch (e) {
      setError(t('admin.error_save_ai'));
    } finally {
      setProcessingId(null);
    }
  };

  const handleSaveYouTubeKey = async (key: string) => {
    try {
      const saveSecrets = httpsCallable(functions, 'saveAiSecrets');
      await saveSecrets({ youtube_api_key: key });
      setYoutubeKeyStatus(prev => ({ ...prev!, hasYouTubeKey: true, source: 'firestore' }));
      setIsYouTubeKeySaved(true);
      setTimeout(() => setIsYouTubeKeySaved(false), 3000);
    } catch (e: any) {
      setError(`Feil ved lagring av nøkkel: ${e.message}`);
    }
  };

  const handleSaveSvvKey = async (key: string) => {
    try {
      const saveSecrets = httpsCallable(functions, 'saveAiSecrets');
      await saveSecrets({ svv_api_key: key });
      setYoutubeKeyStatus(prev => ({ ...prev!, hasSvvKey: true, svvSource: 'firestore' }));
      setIsSvvKeySaved(true);
      setTimeout(() => setIsSvvKeySaved(false), 3000);
    } catch (e: any) {
      setError(`Feil ved lagring av SVV-nøkkel: ${e.message}`);
    }
  };

  const handleSaveMapillaryToken = async (key: string) => {
    try {
      const saveSecrets = httpsCallable(functions, 'saveAiSecrets');
      await saveSecrets({ mapillary_access_token: key });
      setYoutubeKeyStatus(prev => ({ ...prev!, hasMapillaryToken: true, mapillarySource: 'firestore' }));
      setIsMapillaryTokenSaved(true);
      setTimeout(() => setIsMapillaryTokenSaved(false), 3000);
    } catch (e: any) {
      setError(`Feil ved lagring av Mapillary-token: ${e.message}`);
    }
  };

  const handleSaveYrKey = async (key: string) => {
    try {
      const saveSecrets = httpsCallable(functions, 'saveAiSecrets');
      await saveSecrets({ yr_api_key: key });
      setYoutubeKeyStatus(prev => ({ ...prev!, hasYrKey: true, yrSource: 'firestore' }));
      setIsYrKeySaved(true);
      setTimeout(() => setIsYrKeySaved(false), 3000);
    } catch (e: any) {
      setError(`Feil ved lagring av Yr API-nøkkel: ${e.message}`);
    }
  };

  const handleSaveTrafikverketKey = async (key: string) => {
    try {
      const saveSecrets = httpsCallable(functions, 'saveAiSecrets');
      await saveSecrets({ trafikverket_api_key: key });
      setYoutubeKeyStatus(prev => ({ ...prev!, hasTrafikverketKey: true, trafikverketSource: 'firestore' }));
      setIsTrafikverketKeySaved(true);
      setTimeout(() => setIsTrafikverketKeySaved(false), 3000);
    } catch (e: any) {
      setError(`Feil ved lagring av Trafikverket API-nøkkel: ${e.message}`);
    }
  };

  const handleTestImageApis = async () => {
    const fn = httpsCallable(functions, 'testImageApis');
    const res = await fn();
    return res.data;
  };

  const handleBackfillImages = async (batchSize?: number) => {
    const fn = httpsCallable(functions, 'backfillLocationImages');
    const res = await fn({ batchSize: batchSize || 20 });
    return res.data;
  };

  const handleGetAutoImageStats = async () => {
    const fn = httpsCallable(functions, 'getAutoImageStats');
    const res = await fn();
    return res.data;
  };

  const handleChangeAiModel = async (modelId: string) => {
    const prev = aiModel;
    setAiModel(modelId);
    setProcessingId('ai-model-save');
    try {
      await saveSystemConfig('ai_config', { model: modelId });
      setIsModelSaved(true);
      setTimeout(() => setIsModelSaved(false), 3000);
    } catch (e) {
      setAiModel(prev);
      setError(t('admin.error_save_ai'));
    } finally {
      setProcessingId(null);
    }
  };

  const handleChangeImageModel = async (modelId: string) => {
    const prev = imageModel;
    setImageModel(modelId);
    setProcessingId('image-model-save');
    try {
      await saveSystemConfig('ai_config', { image_model: modelId });
      setIsImageModelSaved(true);
      setTimeout(() => setIsImageModelSaved(false), 3000);
    } catch (e) {
      setImageModel(prev);
      setError(t('admin.error_save_ai'));
    } finally {
      setProcessingId(null);
    }
  };

  const handleToggleTesterApps = async (enabled: boolean) => {
    setProcessingId('tester-toggle');
    try {
      await saveSystemConfig('config', { tester_applications_enabled: enabled });
      setTesterEnabled(enabled);
    } catch (e) {
      setError(t('admin.error_update_config'));
    } finally {
      setProcessingId(null);
    }
  };

  const handleToggleTestFlightSync = async (enabled: boolean) => {
    setProcessingId('testflight-toggle');
    try {
      await saveSystemConfig('testflight_sync', { enabled, beta_group_id: testflightBetaGroupId });
      setTestflightSyncEnabled(enabled);
    } catch (e) {
      setError(t('admin.error_update_testflight_sync', 'Kunne ikke oppdatere TestFlight-konfigurasjon.'));
    } finally {
      setProcessingId(null);
    }
  };

  const handleToggleTestFlightPublic = async (pub: boolean) => {
    try {
      await saveSystemConfig('testflight_sync', { public: pub });
      setTestflightPublic(pub);
    } catch (e) {
      setError(t('admin.error_update_testflight_sync', 'Kunne ikke oppdatere TestFlight-konfigurasjon.'));
    }
  };

  const handleSaveTestFlightConfig = async (betaGroupId: string) => {
    try {
      await saveSystemConfig('testflight_sync', { enabled: testflightSyncEnabled, beta_group_id: betaGroupId });
      setTestflightBetaGroupId(betaGroupId);
    } catch (e) {
      setError(t('admin.error_update_testflight_sync', 'Kunne ikke oppdatere TestFlight-konfigurasjon.'));
    }
  };

  const handleRunTestFlightSync = async () => {
    setProcessingId('testflight-sync-run');
    try {
      const callable = httpsCallable(functions, 'runTestFlightSyncManual');
      const res = await callable({});
      const data = res.data as any;
      if (data?.last_sync_result) {
        setTestflightSyncStatus({ last_sync_at: new Date(), last_sync_result: data.last_sync_result });
      }
    } catch (e: any) {
      setError(t('admin.error_testflight_sync', 'Kunne ikke kjøre TestFlight-synk.'));
    } finally {
      setProcessingId(null);
    }
  };


  const handleToggleAdminReevaluateButton = async (enabled: boolean) => {
    setProcessingId('admin-reevaluate-toggle');
    try {
      await saveSystemConfig('config', { show_admin_reevaluate_button: enabled });
      setAdminReevaluateButtonEnabled(enabled);
    } catch (e) {
      setError(t('admin.error_update_admin_button'));
    } finally {
      setProcessingId(null);
    }
  };

  const handleToggleAdminSubscriptionPush = async (enabled: boolean) => {
    setProcessingId('admin-subscription-push-toggle');
    try {
      await saveSystemConfig('config', { admin_push_new_subscription_enabled: enabled });
      setAdminSubscriptionPushEnabled(enabled);
    } catch (e) {
      setError(t('admin.error_update_config'));
    } finally {
      setProcessingId(null);
    }
  };

  const handleToggleNordicCountry = async (country: string, enabled: boolean) => {
    setProcessingId(`nordic-${country}-toggle`);
    setError(null);
    try {
      const savedConfig = await updateNordicConfig(country, enabled);
      setNordicCountryConfig({
        sweden: savedConfig.sweden === true,
        denmark: savedConfig.denmark === true,
        finland: savedConfig.finland === true,
        iceland: savedConfig.iceland === true,
      });
      console.info('Nordic config saved:', { country, enabled, savedConfig });
    } catch (e: any) {
      console.error('handleToggleNordicCountry failed:', {
        country,
        enabled,
        code: e?.code,
        message: e?.message,
        details: e?.details,
      });
      setError(`Kunne ikke oppdatere nordisk konfigurasjon (${e?.code || 'ukjent'}). Se system_config_audit i Firestore.`);
    } finally {
      setProcessingId(null);
    }
  };

  const handleToggleExternalSync = async (enabled: boolean) => {
    setProcessingId('external-sync-toggle');
    try {
      await saveSystemConfig('external_sync', {
        enabled,
        sources: externalSyncSources,
        regions: externalSyncRegions
      });
      setExternalSyncEnabled(enabled);
    } catch (e) {
      setError(t('admin.error_update_external_sync'));
    } finally {
      setProcessingId(null);
    }
  };

  const handleRunExternalSync = async () => {
    setProcessingId('external-sync-run');
    try {
      const res = await runExternalSpotsSync();
      if ((res as any)?.skipped) {
        setError(t('admin.error_external_sync_skipped', { reason: (res as any)?.reason || 'unknown' }));
      }
    } catch (e: any) {
      setError(t('admin.error_external_sync_start', { code: e?.code || 'unknown' }));
    } finally {
      setProcessingId(null);
    }
  };

  const handleExternalSyncRegionChange = (
    index: number,
    field: 'id' | 'name' | 'enabled' | 'latMin' | 'lonMin' | 'latMax' | 'lonMax',
    value: string | boolean
  ) => {
    const updatedRegions = externalSyncRegions.map((r, i) => {
      if (i !== index) return r;
      if (field === 'id' || field === 'name') {
        return { ...r, [field]: String(value) };
      }
      if (field === 'enabled') {
        return { ...r, enabled: Boolean(value) };
      }
      return { ...r, bounds: { ...r.bounds, [field]: Number(value) } };
    });
    setExternalSyncRegions(updatedRegions);

    // Auto-save when toggling the enabled checkbox
    if (field === 'enabled') {
      const cleanRegions = updatedRegions
        .map((r) => ({
          id: String(r.id || '').trim(),
          name: String(r.name || '').trim(),
          enabled: r.enabled !== false,
          bounds: { latMin: Number(r.bounds.latMin), lonMin: Number(r.bounds.lonMin), latMax: Number(r.bounds.latMax), lonMax: Number(r.bounds.lonMax) }
        }))
        .filter((r) => r.id && [r.bounds.latMin, r.bounds.lonMin, r.bounds.latMax, r.bounds.lonMax].every((n) => Number.isFinite(n)));
      saveSystemConfig('external_sync', { enabled: externalSyncEnabled, sources: externalSyncSources, regions: cleanRegions })
        .catch(() => setError(t('admin.error_save_sync_regions')));
    }
  };

  const handleAddExternalSyncRegion = () => {
    setExternalSyncRegions((prev) => [
      ...prev,
      {
        id: `region-${Date.now()}`,
        name: t('admin.new_region'),
        enabled: true,
        bounds: { latMin: 60, lonMin: 10, latMax: 61, lonMax: 11 }
      }
    ]);
  };

  const slugifyRegionId = (value: string) =>
    String(value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 80) || `region-${Date.now()}`;

  const handleAddRegionFromSearchResult = (result: { display_name: string; boundingbox?: string[]; lat?: string; lon?: string }) => {
    const bb = Array.isArray(result.boundingbox) ? result.boundingbox : [];
    let latMin = Number(bb[0]);
    let latMax = Number(bb[1]);
    let lonMin = Number(bb[2]);
    let lonMax = Number(bb[3]);

    if (![latMin, latMax, lonMin, lonMax].every((n) => Number.isFinite(n))) {
      const lat = Number(result.lat);
      const lon = Number(result.lon);
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
        setError(t('admin.error_search_missing_area'));
        return;
      }
      // Fallback: liten bbox rundt punktet
      latMin = lat - 0.08;
      latMax = lat + 0.08;
      lonMin = lon - 0.12;
      lonMax = lon + 0.12;
    }

    if (latMin > latMax) [latMin, latMax] = [latMax, latMin];
    if (lonMin > lonMax) [lonMin, lonMax] = [lonMax, lonMin];

    const display = String(result.display_name || '').trim();
    const firstPart = display.split(',')[0]?.trim() || t('admin.new_region');
    const idBase = slugifyRegionId(firstPart);
    let id = idBase;
    let suffix = 2;
    const existingIds = new Set(externalSyncRegions.map((r) => r.id));
    while (existingIds.has(id)) {
      id = `${idBase}-${suffix++}`;
    }

    setExternalSyncRegions((prev) => [
      ...prev,
      {
        id,
        name: firstPart,
        enabled: true,
        bounds: { latMin, lonMin, latMax, lonMax }
      }
    ]);
    setRegionSearchQuery('');
    setRegionSearchResults([]);
  };


  const handleAddRegionFromCurrentView = () => {
    if (!currentViewBounds) {
      setError(t('admin.error_map_view_unavailable'));
      return;
    }
    const idBase = `kartutsnitt-${Date.now()}`;
    setExternalSyncRegions((prev) => [
      ...prev,
      {
        id: idBase,
        name: t('admin.map_view_name'),
        enabled: true,
        bounds: {
          latMin: Number(currentViewBounds.latMin),
          lonMin: Number(currentViewBounds.lonMin),
          latMax: Number(currentViewBounds.latMax),
          lonMax: Number(currentViewBounds.lonMax)
        }
      }
    ]);
  };

  const handleToggleExternalSyncSource = async (source: 'nvdb' | 'osmAmenities' | 'osmCamp') => {
    const newSources = { ...externalSyncSources, [source]: !externalSyncSources[source] };
    setExternalSyncSources(newSources);
    try {
      const cleanRegions = externalSyncRegions
        .map((r) => ({
          id: String(r.id || '').trim(),
          name: String(r.name || '').trim(),
          enabled: r.enabled !== false,
          bounds: { latMin: Number(r.bounds.latMin), lonMin: Number(r.bounds.lonMin), latMax: Number(r.bounds.latMax), lonMax: Number(r.bounds.lonMax) }
        }))
        .filter((r) => r.id && [r.bounds.latMin, r.bounds.lonMin, r.bounds.latMax, r.bounds.lonMax].every((n) => Number.isFinite(n)));
      await saveSystemConfig('external_sync', { enabled: externalSyncEnabled, sources: newSources, regions: cleanRegions });
    } catch (e) {
      setExternalSyncSources(prev => ({ ...prev, [source]: !prev[source] }));
      setError(t('admin.error_save_sync_regions'));
    }
  };

  const handleRemoveExternalSyncRegion = (index: number) => {
    setExternalSyncRegions((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSaveExternalSyncRegions = async () => {
    if (!externalSyncRegionValidation.isValid) {
      setError(t('admin.error_invalid_regions', { issue: externalSyncRegionValidation.issues[0] }));
      return;
    }
    setProcessingId('external-sync-save-regions');
    try {
      const cleanRegions = externalSyncRegions
        .map((r) => ({
          id: String(r.id || '').trim(),
          name: String(r.name || '').trim(),
          enabled: r.enabled !== false,
          bounds: {
            latMin: Number(r.bounds.latMin),
            lonMin: Number(r.bounds.lonMin),
            latMax: Number(r.bounds.latMax),
            lonMax: Number(r.bounds.lonMax)
          }
        }))
        .filter((r) => r.id && [r.bounds.latMin, r.bounds.lonMin, r.bounds.latMax, r.bounds.lonMax].every((n) => Number.isFinite(n)));

      await saveSystemConfig('external_sync', {
        enabled: externalSyncEnabled,
        sources: externalSyncSources,
        regions: cleanRegions
      });
    } catch (e) {
      setError(t('admin.error_save_sync_regions'));
    } finally {
      setProcessingId(null);
    }
  };

  const [confirmResetAi, setConfirmResetAi] = useState(false);
  const handleResetAiConfig = () => {
    if (!confirmResetAi) { setConfirmResetAi(true); return; }
    setAiPrompt(getDefaultAiPrompt());
    setConfirmResetAi(false);
  };

  const handleDismissToolsIntro = () => {
    const key = `fricamp_admin_tools_intro_dismissed_${currentUser?.uid || 'anon'}`;
    window.localStorage.setItem(key, '1');
    setShowToolsIntro(false);
  };

  const groupedRoutes = useMemo(() => {
    const targetRoutes = activeTab === 'pending' ? pendingRoutes : routes;
    const filteredRoutes = activeTab === 'routes' && routeStatusFilter !== 'all'
      ? targetRoutes.filter(r => r.status === routeStatusFilter)
      : targetRoutes;
    const groups: Record<string, GpxRoute[]> = {
      [t('admin.regions.north')]: [],
      [t('admin.regions.central')]: [],
      [t('admin.regions.south')]: [],
      [t('admin.regions.unsorted')]: []
    };
    const regionLabelMap: Record<string, string> = {
      'north': t('admin.regions.north'),
      'central': t('admin.regions.central'),
      'south': t('admin.regions.south')
    };
    filteredRoutes.forEach(r => {
      const label = r.region ? regionLabelMap[r.region] : null;
      if (label && groups[label]) {
        groups[label].push(r);
      } else {
        groups[t('admin.regions.unsorted')].push(r);
      }
    });
    return groups;
  }, [routes, pendingRoutes, activeTab, routeStatusFilter]);

  const testerThreadByUid = useMemo(() => {
    const byUid = new Map<string, TesterThread>();
    testerThreads.forEach((thread) => {
      if (thread.testerUid) byUid.set(thread.testerUid, thread);
      else if (thread.id) byUid.set(thread.id, thread);
    });
    return byUid;
  }, [testerThreads]);

  const sortedApprovedTesters = useMemo(() => {
    const labelForSort = (tester: TesterProfile): string =>
      (tester.displayName || tester.email || tester.uid || '').toLowerCase();

    return [...approvedTesters].sort((a, b) => {
      const threadA = testerThreadByUid.get(a.uid);
      const threadB = testerThreadByUid.get(b.uid);
      const unreadA = (threadA?.unreadForAdmin || 0) > 0 ? 1 : 0;
      const unreadB = (threadB?.unreadForAdmin || 0) > 0 ? 1 : 0;

      if (unreadA !== unreadB) return unreadB - unreadA;

      const timeA = getThreadSortValue(threadA?.lastMessageAt);
      const timeB = getThreadSortValue(threadB?.lastMessageAt);
      if (timeA !== timeB) return timeB - timeA;

      return labelForSort(a).localeCompare(labelForSort(b), 'nb');
    });
  }, [approvedTesters, testerThreadByUid]);

  const ownInboxUnread = useMemo(() => {
    if (!currentUser?.uid) return 0;
    const ownThread = testerThreads.find((t) => t.testerUid === currentUser.uid);
    return ownThread?.unreadForTester || 0;
  }, [currentUser?.uid, testerThreads]);

  const isDesktopAdminLayout = !inboxOnly && !isMobileViewport;
  const ADMIN_TAB_META = getAdminTabMeta(t);
  const activeTabMeta = ADMIN_TAB_META[activeTab];
  const activeTabDescription = inboxOnly ? t('admin.messages_from_testers') : activeTabMeta.subtitle;
  const totalTesterUnreadForAdmin = useMemo(
    () => testerThreads.reduce((sum, thread) => sum + (thread.unreadForAdmin || 0), 0),
    [testerThreads]
  );

  const handleUpdateExternalSyncRegion = handleExternalSyncRegionChange as any;
  const handleApplyRegionSearchResult = handleAddRegionFromSearchResult;
  const handleSaveExternalSyncSources = handleSaveExternalSyncRegions;
  const tabCounts = useMemo(() => ({
    spots: deleteSuggestions.length,
    suggestions: categorySuggestions.length,
    pending: pendingRoutes.length,
    routes: routes.length,
    testers: testerRequests.length + totalTesterUnreadForAdmin
  }), [deleteSuggestions.length, categorySuggestions.length, pendingRoutes.length, routes.length, testerRequests.length, totalTesterUnreadForAdmin]);

  const handleTabChange = (tab: AdminTab) => {
    setActiveTab(tab);
    setError(null);
  };

  const toggleRegion = (region: RouteRegion) => {
    setExpandedRegions(prev => {
      const next = new Set(prev);
      if (next.has(region)) next.delete(region);
      else next.add(region);
      return next;
    });
  };

  const loadRouteGeometries = (routesList: GpxRoute[]) => {
    routesList.forEach(async (r) => {
      try {
        if (routeGeometries[r.id]) return;
        const text = await fetchGpxWithProxy(r.gpxUrl);
        const dom = new DOMParser().parseFromString(text, "text/xml");
        const trkpts = dom.getElementsByTagName("trkpt");
        const coords: [number, number][] = [];
        for (let i = 0; i < Math.min(trkpts.length, 500); i += 2) {
          const lat = parseFloat(trkpts[i].getAttribute("lat")!);
          const lon = parseFloat(trkpts[i].getAttribute("lon")!);
          if (!isNaN(lat) && !isNaN(lon)) coords.push([lat, lon]);
        }
        if (coords.length > 0) {
          setRouteGeometries(prev => ({ ...prev, [r.id]: coords }));
          if (!r.region) {
            const region = determineRegion(coords[0][0]);
            await updateGpxRoute(r.id, { region });
            setRoutes(prev => prev.map(route => route.id === r.id ? { ...route, region } : route));
          }
        }
      } catch (e) { console.warn("Could not load geometry for route", r.id); }
    });
  }

  const handleResolveTesterRequest = async (request: TesterRequest, status: 'approved' | 'rejected', reason?: string, validDays?: number) => {
    setProcessingId(request.id);
    try {
      await resolveTesterRequest(request.id, status, reason, validDays);
      setTesterRequests(prev => prev.filter(r => r.id !== request.id));
    } catch (e: any) {
      setError(t('admin.error_process_request', { code: e?.code || 'unknown' }));
    } finally {
      setProcessingId(null);
    }
  };

  const handleOpenTesterInbox = (tester: TesterProfile) => {
    setInboxTester(tester);
    setTesterThreads(prev => prev.map(t => t.testerUid === tester.uid ? { ...t, unreadForAdmin: 0 } : t));
  };

  const handleOpenUserInbox = (user: AppUser) => {
    setInboxUser({ uid: user.uid, displayName: user.displayName, email: user.email, photoURL: user.photoURL });
  };

  const verifiedFeedUrl = 'https://us-central1-fricamp-2810e.cloudfunctions.net/verifiedSpotsFeed?limit=3';
  const handleCopyFeedUrl = async () => {
    try {
      await navigator.clipboard.writeText(verifiedFeedUrl);
      setFeedCopied(true);
      setTimeout(() => setFeedCopied(false), 1800);
    } catch (e) {
      window.prompt('Kopier feed-lenken:', verifiedFeedUrl);
    }
  };

  const sitemapUrl = 'https://app.fricamp.com/sitemap.xml';
  const handleCopySitemapUrl = async () => {
    try {
      await navigator.clipboard.writeText(sitemapUrl);
      setSitemapCopied(true);
      setTimeout(() => setSitemapCopied(false), 1800);
    } catch (e) {
      window.prompt('Kopier sitemap-lenken:', sitemapUrl);
    }
  };

  const loadSeoReport = async () => {
    setSeoReportLoading(true);
    try {
      const report = await fetchSeoMetricsReport();
      setSeoReport(report);
    } catch (e) {
      console.warn('Could not fetch SEO report', e);
      setError(t('admin.error_seo_report'));
    } finally {
      setSeoReportLoading(false);
    }
  };

  const handleEndTesterByAdmin = async (tester: TesterProfile) => {
    setProcessingId(`end-${tester.uid}`);
    try {
      await endTesterProfile(tester.uid);
      setApprovedTesters(prev => prev.filter(t => t.uid !== tester.uid));
    } catch (e: any) {
      setError(t('admin.error_end_tester', { code: e?.code || 'unknown' }));
    } finally {
      setProcessingId(null);
    }
  };

  const handlePromoteToTester = async (user: AppUser, validDays: number = 90) => {
    setProcessingId(`promote-${user.uid}`);
    try {
      await promoteUserToTester(user.uid, validDays);
      const validUntilDate = new Date();
      validUntilDate.setDate(validUntilDate.getDate() + validDays);
      // Move user from allUsers to approvedTesters
      setApprovedTesters(prev => [
        ...prev,
        { 
          uid: user.uid, 
          displayName: user.displayName, 
          email: user.email, 
          photoURL: user.photoURL, 
          isTester: true, 
          platform: user.platform,
          tester_joined_at: new Date(),
          tester_valid_until: validUntilDate
        },
      ]);
      // Refresh allUsers to reflect change
      setAllUsers(prev => prev.map(u => u.uid === user.uid ? { ...u, isTester: true, tester_valid_until: validUntilDate } : u));
    } catch (e: any) {
      setError(t('admin.error_promote_tester', { code: e?.code || 'unknown' }));
    } finally {
      setProcessingId(null);
    }
  };

  const handleResolveDeletion = async () => {
    if (!confirmingSpotAction) return;
    const { id, action } = confirmingSpotAction;
    const suggestion = deleteSuggestions.find(s => s.id === id);
    if (!suggestion) return;

    setProcessingId(id);
    setError(null);
    try {
      await resolveDeletionSuggestion(suggestion, action);
      setDeleteSuggestions(prev => prev.filter(s => s.id !== id));
      if (action === 'accept' && onSpotRemoved) {
        onSpotRemoved(suggestion.spotId);
      }
      setConfirmingSpotAction(null);
    } catch (err) {
      setError(t('admin.error_action_failed'));
    } finally {
      setProcessingId(null);
    }
  };

  const handleResolveCategory = async (suggestion: CategorySuggestion, action: 'approve' | 'reject') => {
    setProcessingId(suggestion.id);
    try {
      // Features to remove: This assumes CATEGORIES are constants we know about. 
      // In a real app we'd import CATEGORIES or pass this data.
      // For now, hardcode the common ones from AddSpotModal:
      const commonFeatures = ["FRICAMP", "GAPAHUK", "UTSIKT", "BÅLPLASS", "TOALETT", "BADEPLASS", "VANN", "PICNIC", "KAI/BRYGGE", "SNUPLASS"];

      await resolveCategorySuggestion(suggestion, action, commonFeatures);
      setCategorySuggestions(prev => prev.filter(s => s.id !== suggestion.id));
    } catch (e) {
      setError(t('admin.error_category_suggestion'));
    } finally {
      setProcessingId(null);
    }
  };

  const handleRouteApproval = async (route: GpxRoute, approved: boolean) => {
    setProcessingId(route.id);
    try {
      if (approved) {
        await updateGpxRoute(route.id, { status: 'public' });
        setRoutes(prev => [route, ...prev]);
      } else {
        await updateGpxRoute(route.id, { status: 'rejected', rejectionReason: t('admin.rejected_by_admin') });
      }
      setPendingRoutes(prev => prev.filter(r => r.id !== route.id));
      setReviewingRouteId(null);
    } catch (e) {
      setError(t('admin.error_process_route'));
    } finally {
      setProcessingId(null);
    }
  }

  const handleMakeRoutePublic = async (route: GpxRoute) => {
    setProcessingId(route.id);
    try {
      await updateGpxRoute(route.id, { status: 'public' });
      setRoutes(prev => prev.map(r => r.id === route.id ? { ...r, status: 'public' as const } : r));
    } catch (e) {
      setError(t('admin.error_process_route'));
    } finally {
      setProcessingId(null);
    }
  };

  const startEditRoute = (route: GpxRoute) => {
    setEditingRouteId(route.id);
    setEditFields({ name: route.name, description: route.description, difficulty: route.difficulty });
  };

  const saveRouteUpdate = async (id: string) => {
    setProcessingId(id);
    setError(null);
    try {
      await updateGpxRoute(id, editFields);
      setRoutes(prev => prev.map(r => r.id === id ? { ...r, ...editFields } : r));
      setEditingRouteId(null);
    } catch (err) {
      setError(t('admin.error_update_failed'));
    } finally {
      setProcessingId(null);
    }
  };

  const confirmDeleteRoute = async (id: string) => {
    setProcessingId(id);
    setError(null);
    try {
      await deleteRoute(id);
      setRoutes(prev => prev.filter(r => r.id !== id));
      setDeletingRouteId(null);
    } catch (err) {
      setError(t('admin.error_delete_failed'));
    } finally {
      setProcessingId(null);
    }
  };

  const executeClearArea = async () => {
    if (!onClearArea) return;
    setIsClearingArea(true);
    try {
      await onClearArea();
      toast(t('admin.area_cleared'), 'success');
      setConfirmClearArea(false);
    } catch (e) {
      toast(t('admin.error_delete_something_wrong'), 'error');
    } finally {
      setIsClearingArea(false);
    }
  };

  const executeReevaluation = async () => {
    if (!onReevaluateArea) return;
    onClose(); // Close panel to show progress overlay
    try {
      await onReevaluateArea();
    } catch (e) {
      toast(t('admin.error_reevaluation_start'), 'error');
    }
  };

  const adminMarkerIcon = L.divIcon({
    className: 'admin-marker',
    html: `<div style="width: 14px; height: 14px; background: #ef4444; border: 3px solid white; border-radius: 50%; box-shadow: 0 0 15px rgba(239, 68, 68, 0.6);"></div>`,
    iconSize: [14, 14],
    iconAnchor: [7, 7]
  });

  const renderSpotPreview = (suggestion: DeletionSuggestion) => {
    if (!suggestion.location) return null;

    return (
      <div className="h-64 w-full relative bg-[var(--bg-base)] border-b border-[var(--border-primary)]">
        <MapContainer
          key={`${suggestion.id}-preview-map`}
          center={[suggestion.location.latitude, suggestion.location.longitude]}
          zoom={14}
          zoomControl={false}
          className="h-full w-full z-10"
        >
          <ForceMapResize />
          <PreviewMapController center={[suggestion.location.latitude, suggestion.location.longitude]} />
          <TileLayer url={MAP_LAYERS.SATELLITE} attribution='&copy; ESRI' />
          <Pane name="labels" style={{ zIndex: 650 }}>
            <TileLayer url={MAP_LAYERS.SATELLITE_LABELS} attribution='&copy; ESRI' />
          </Pane>
          <Marker position={[suggestion.location.latitude, suggestion.location.longitude]} icon={adminMarkerIcon} />
        </MapContainer>
        <button onClick={() => onViewSpot(suggestion.spotId, suggestion.location!)} className="absolute bottom-4 right-4 p-3 bg-emerald-600 text-white rounded-2xl shadow-2xl hover:scale-105 transition-all flex items-center gap-2 z-30 group-hover:bg-emerald-500">
          <MapIcon size={16} /><span className="text-2xs font-black uppercase tracking-widest">{t('admin.view_on_main_map')}</span>
        </button>
      </div>
    );
  };

  const handleRouteEditFieldChange = (field: 'name' | 'description' | 'difficulty', value: string) => {
    setEditFields((prev) => ({
      ...prev,
      [field]: field === 'difficulty' ? (value as RouteDifficulty) : value
    }));
  };

  const renderRouteMap = (route: GpxRoute) => {
    const geometry = routeGeometries[route.id];
    if (!geometry || geometry.length <= 1) return null;

    return (
      <div className="h-48 w-full relative bg-[var(--bg-base)] border-b border-[var(--border-primary)]">
        <MapContainer
          center={geometry[0]}
          zoom={12}
          zoomControl={false}
          className="h-full w-full z-10"
          dragging={false}
          scrollWheelZoom={false}
        >
          <ForceMapResize />
          <PreviewMapController bounds={L.latLngBounds(geometry)} />
          <TileLayer url={MAP_LAYERS.SATELLITE} attribution='&copy; ESRI' />
          <Polyline positions={geometry} pathOptions={{ color: '#3b82f6', weight: 4 }} />
        </MapContainer>
      </div>
    );
  };

  const ActiveTabIcon = activeTabMeta.icon;

  return (
    <div className={`fixed left-0 right-0 bottom-0 z-[10005] flex ${inboxOnly ? 'items-end sm:items-center' : 'items-center'} justify-center px-4 pb-4`} style={{ top: 'env(safe-area-inset-top, 0px)' }}>
      <div className="absolute inset-0 bg-black/90 backdrop-blur-md" onClick={onClose} />
      <div className={`relative w-full ${inboxOnly ? 'sm:max-w-3xl max-w-full' : isDesktopAdminLayout ? 'max-w-[min(96vw,1320px)]' : 'max-w-3xl'} bg-[var(--bg-base)] border border-[var(--border-secondary)] ${inboxOnly ? 'sm:rounded-modal rounded-t-panel' : 'rounded-modal'} shadow-3xl overflow-hidden flex flex-col ${inboxOnly ? 'max-h-[92dvh]' : isDesktopAdminLayout ? 'h-[92vh]' : 'max-h-[85vh]'} animate-in zoom-in-95 duration-300`} style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>

        {/* Header */}
        <div className="flex flex-col bg-[var(--bg-input)] border-b border-[var(--border-primary)]">
          <div className={`${inboxOnly ? 'p-4 pb-3' : 'p-5 pb-3'} flex justify-between items-start`}>
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-xl border ${activeTabMeta.iconTone}`}>
                <ActiveTabIcon size={18} />
              </div>
              <div>
                <h2 className="text-xl font-black text-[var(--text-primary)] tracking-tight uppercase">{inboxOnly ? t('admin.title_inbox') : t('admin.title_control')}</h2>
                <p className="text-2xs text-[var(--text-muted)] font-bold uppercase tracking-widest mt-1">
                  {activeTabDescription}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {inboxOnly && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)]">
                  <span className="text-3xs font-black uppercase tracking-widest text-[var(--text-secondary)]">{t('admin.tester_applications')}</span>
                  <button
                    onClick={() => handleToggleTesterApps(!testerEnabled)}
                    disabled={processingId === 'tester-toggle'}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none ${testerEnabled ? 'bg-blue-600' : 'bg-[var(--bg-input)]'} ${processingId === 'tester-toggle' ? 'opacity-60' : ''}`}
                    title={testerEnabled ? t('admin.disable_tester_apps') : t('admin.enable_tester_apps')}
                  >
                    <span
                      className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${testerEnabled ? 'translate-x-5' : 'translate-x-0.5'}`}
                    />
                  </button>
                </div>
              )}
              {!inboxOnly && currentUser && !isDesktopAdminLayout && (
                <button
                  onClick={() => setShowOwnInbox(true)}
                  className="px-3 py-2.5 bg-emerald-600/20 hover:bg-emerald-600/30 border border-emerald-500/30 text-emerald-300 rounded-xl text-3xs font-black uppercase tracking-widest flex items-center gap-2 transition-all active:scale-95"
                >
                  <MessageSquare size={13} />
                  {t('admin.my_inbox')}
                  {ownInboxUnread > 0 && (
                    <span className="px-1.5 py-0.5 rounded-full bg-red-500 text-white text-4xs leading-none">
                      {ownInboxUnread}
                    </span>
                  )}
                </button>
              )}
              {!inboxOnly && !isDesktopAdminLayout && (activeTab === 'routes' || activeTab === 'pending') && (
                <button
                  onClick={onOpenRouteUpload}
                  className="px-5 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-xl text-3xs font-black uppercase tracking-widest flex items-center gap-2 transition-all active:scale-95 shadow-lg"
                >
                  <Route size={14} /> {t('admin.new_route')}
                </button>
              )}
              <button onClick={onClose} className="p-2.5 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors bg-[var(--bg-surface-alt)] rounded-xl">
                <X size={20} />
              </button>
            </div>
          </div>

          {/* Navigation Tabs */}
          {!inboxOnly && !isDesktopAdminLayout && (
            <AdminTopTabs
              activeTab={activeTab}
              onSelectTab={handleTabChange}
              tabCounts={tabCounts}
              hasUnreadTesterThreads={testerThreads.some(t => (t.unreadForAdmin || 0) > 0)}
            />
          )}
        </div>

        {/* Global Feilmelding */}
        {error && (
          <div className="mx-8 mt-6 p-4 bg-red-500/10 border border-red-500/20 rounded-2xl flex items-center gap-3 text-red-400 animate-in slide-in-from-top-2">
            <AlertCircle size={18} />
            <p className="text-2xs font-black uppercase tracking-widest">{error}</p>
            <button onClick={() => setError(null)} className="ml-auto p-1 hover:bg-[var(--bg-surface-alt)] rounded"><X size={14} /></button>
          </div>
        )}

        {/* Innhold */}
        <div className={`flex-1 min-h-0 ${isDesktopAdminLayout ? 'flex flex-col overflow-hidden' : 'overflow-y-auto'} ${isDesktopAdminLayout ? '' : 'custom-scrollbar'}`}>
          <div className={isDesktopAdminLayout ? 'flex-1 min-h-0 overflow-hidden grid grid-cols-[260px_minmax(0,1fr)]' : 'min-h-full'}>
            {isDesktopAdminLayout && (
              <AdminDesktopSidebar
                activeTab={activeTab}
                onSelectTab={handleTabChange}
                tabCounts={tabCounts}
                totalTesterUnreadForAdmin={totalTesterUnreadForAdmin}
              />
            )}

            <div className={`${isDesktopAdminLayout ? 'min-w-0 min-h-0 h-full flex flex-col overflow-hidden' : ''}`}>
              {isDesktopAdminLayout && (
                <AdminDesktopContextBar
                  activeTab={activeTab}
                  onOpenRouteUpload={onOpenRouteUpload}
                  onShowOwnInbox={() => setShowOwnInbox(true)}
                  canShowOwnInbox={Boolean(currentUser)}
                  onRefreshSeoReport={loadSeoReport}
                  seoReportLoading={seoReportLoading}
                />
              )}

              <div className={isDesktopAdminLayout ? 'flex-1 h-0 min-h-0 overflow-y-auto [overscroll-behavior:contain] touch-pan-y p-6 space-y-6 custom-scrollbar' : `${inboxOnly ? 'p-4' : 'p-6'} space-y-6 custom-scrollbar`}>
                {isLoading ? (
                  <div className="flex flex-col items-center justify-center py-20 space-y-4">
                    <Loader2 className="animate-spin text-emerald-500" size={32} />
                    <p className="text-2xs font-black text-[var(--text-muted)] uppercase tracking-widest">{t('admin.loading_system')}</p>
                  </div>
                ) : activeTab === 'ai' ? (
                  <AdminAiConfigPanel
                    aiPrompt={aiPrompt}
                    onAiPromptChange={setAiPrompt}
                    onResetAiConfig={handleResetAiConfig}
                    onSaveAiConfig={handleSaveAiConfig}
                    processingId={processingId}
                    isAiSaved={isAiSaved}
                    aiModel={aiModel}
                    availableModels={availableModels}
                    onChangeAiModel={handleChangeAiModel}
                    isModelSaved={isModelSaved}
                    articlePrompt={articlePrompt}
                    onArticlePromptChange={setArticlePrompt}
                    onSaveArticlePrompt={handleSaveArticlePrompt}
                    isArticlePromptSaved={isArticlePromptSaved}
                    youtubeKeyStatus={youtubeKeyStatus}
                    onSaveYouTubeKey={handleSaveYouTubeKey}
                    isYouTubeKeySaved={isYouTubeKeySaved}
                    imageModel={imageModel}
                    onChangeImageModel={handleChangeImageModel}
                    isImageModelSaved={isImageModelSaved}
                    onSaveSvvKey={handleSaveSvvKey}
                    isSvvKeySaved={isSvvKeySaved}
                    onSaveMapillaryToken={handleSaveMapillaryToken}
                    isMapillaryTokenSaved={isMapillaryTokenSaved}
                    onSaveYrKey={handleSaveYrKey}
                    isYrKeySaved={isYrKeySaved}
                    onSaveTrafikverketKey={handleSaveTrafikverketKey}
                    isTrafikverketKeySaved={isTrafikverketKeySaved}
                    onTestImageApis={handleTestImageApis}
                    onBackfillImages={handleBackfillImages}
                    onGetAutoImageStats={handleGetAutoImageStats}
                  />
                ) : activeTab === 'privacy' ? (
                  <AdminPrivacyPanel
                    privacyContent={privacyContent}
                    onPrivacyChange={(field, value) => setPrivacyContent((p) => ({ ...p, [field]: value }))}
                    onSavePrivacyPolicy={handleSavePrivacyPolicy}
                    termsContent={termsContent}
                    onTermsChange={(field, value) => setTermsContent((p) => ({ ...p, [field]: value }))}
                    onSaveTerms={handleSaveTerms}
                    processingId={processingId}
                    isPrivacySaved={isPrivacySaved}
                    isTermsSaved={isTermsSaved}
                  />
                ) : activeTab === 'testers' ? (
                  <AdminTestersPanel
                    currentUser={currentUser}
                    testerRequests={testerRequests}
                    approvedTesters={approvedTesters}
                    sortedApprovedTesters={sortedApprovedTesters}
                    testerThreadByUid={testerThreadByUid}
                    isMobileViewport={isMobileViewport}
                    processingId={processingId}
                    testerEnabled={testerEnabled}
                    onToggleTesterApps={handleToggleTesterApps}
                    onOpenTesterInbox={handleOpenTesterInbox}
                    onEndTesterByAdmin={handleEndTesterByAdmin}
                    onResolveTesterRequest={handleResolveTesterRequest}
                    testflightSyncEnabled={testflightSyncEnabled}
                    testflightBetaGroupId={testflightBetaGroupId}
                    testflightPublic={testflightPublic}
                    testflightSyncStatus={testflightSyncStatus}
                    onToggleTestFlightSync={handleToggleTestFlightSync}
                    onToggleTestFlightPublic={handleToggleTestFlightPublic}
                    onSaveTestFlightConfig={handleSaveTestFlightConfig}
                    onRunTestFlightSync={handleRunTestFlightSync}
                    allUsers={allUsers}
                    onOpenUserInbox={handleOpenUserInbox}
                    onPromoteToTester={handlePromoteToTester}
                  />
                ) : activeTab === 'tools' ? (
                  <AdminToolsPanel
                    activeTab={activeTab}
                    showToolsIntro={showToolsIntro}
                    handleDismissToolsIntro={handleDismissToolsIntro}
                    showSystemSettingsHelp={showSystemSettingsHelp}
                    setShowSystemSettingsHelp={setShowSystemSettingsHelp}
                    showExternalSyncHelp={showExternalSyncHelp}
                    setShowExternalSyncHelp={setShowExternalSyncHelp}
                    adminReevaluateButtonEnabled={adminReevaluateButtonEnabled}
                    handleToggleAdminReevaluateButton={handleToggleAdminReevaluateButton}
                    adminSubscriptionPushEnabled={adminSubscriptionPushEnabled}
                    handleToggleAdminSubscriptionPush={handleToggleAdminSubscriptionPush}
                    externalSyncEnabled={externalSyncEnabled}
                    handleToggleExternalSync={handleToggleExternalSync}
                    handleRunExternalSync={handleRunExternalSync}
                    externalSyncStatus={externalSyncStatus}
                    externalSyncSources={externalSyncSources}
                    setExternalSyncSources={setExternalSyncSources}
                    handleToggleExternalSyncSource={handleToggleExternalSyncSource}
                    handleSaveExternalSyncSources={handleSaveExternalSyncRegions}
                    externalSyncRegions={externalSyncRegions}
                    externalSyncRegionValidation={externalSyncRegionValidation}
                    handleAddExternalSyncRegion={handleAddExternalSyncRegion}
                    handleUpdateExternalSyncRegion={handleExternalSyncRegionChange}
                    handleRemoveExternalSyncRegion={handleRemoveExternalSyncRegion}
                    handleSaveExternalSyncRegions={handleSaveExternalSyncRegions}
                    regionSearchQuery={regionSearchQuery}
                    setRegionSearchQuery={setRegionSearchQuery}
                    regionSearchLoading={regionSearchLoading}
                    regionSearchResults={regionSearchResults}
                    handleApplyRegionSearchResult={handleApplyRegionSearchResult}
                    processingId={processingId}
                    verifiedFeedUrl={verifiedFeedUrl}
                    handleCopyFeedUrl={handleCopyFeedUrl}
                    feedCopied={feedCopied}
                    sitemapUrl={sitemapUrl}
                    handleCopySitemapUrl={handleCopySitemapUrl}
                    sitemapCopied={sitemapCopied}
                    loadSeoReport={loadSeoReport}
                    seoReportLoading={seoReportLoading}
                    seoReport={seoReport}
                    confirmClearArea={confirmClearArea}
                    setConfirmClearArea={setConfirmClearArea}
                    executeClearArea={executeClearArea}
                    isClearingArea={isClearingArea}
                    executeReevaluation={executeReevaluation}
                    handleAddRegionFromCurrentView={handleAddRegionFromCurrentView}
                    nordicCountryConfig={nordicCountryConfig}
                    handleToggleNordicCountry={handleToggleNordicCountry}
                  />
                ) : activeTab === 'errors' ? (
                  <AdminErrorReportsPanel />
                ) : activeTab === 'prescan' ? (
                  <AdminPrescanDashboard />
                ) : activeTab === 'imagescan' ? (
                  <AdminImageScanDashboard />
                ) : activeTab === 'googlescan' ? (
                  <AdminGoogleScanPanel />
                ) : activeTab === 'campaigns' ? (
                  <AdminCampaignsPanel onViewSpot={onViewSpot} />
                ) : activeTab === 'articles' ? (
                  <div className="space-y-4">
                    <AiUsagePanel />
                    <AdminArticlesPanel />
                  </div>
                ) : activeTab === 'creators' ? (
                  <AdminCreatorsPanel />
                ) : activeTab === 'messages' ? (
                  <AdminMessagesPanel />
                ) : activeTab === 'spots' ? (
                  <AdminSpotsPanel
                    deleteSuggestions={deleteSuggestions}
                    confirmingSpotAction={confirmingSpotAction}
                    processingId={processingId}
                    renderSpotPreview={renderSpotPreview}
                    onBeginSpotAction={(id, action) => setConfirmingSpotAction({ id, action })}
                    onCancelSpotAction={() => setConfirmingSpotAction(null)}
                    onResolveDeletion={handleResolveDeletion}
                  />
                ) : activeTab === 'suggestions' ? (
                  <AdminSuggestionsPanel
                    categorySuggestions={categorySuggestions}
                    processingId={processingId}
                    onResolveCategory={handleResolveCategory}
                  />
                ) : (
                  <>
                    {activeTab === 'routes' && (
                      <div className="flex gap-2 mb-4">
                        {(['all', 'public', 'private'] as const).map(f => (
                          <button key={f} onClick={() => setRouteStatusFilter(f)} className={`px-3 py-1.5 rounded-lg text-2xs font-black uppercase tracking-widest transition-all ${routeStatusFilter === f ? 'bg-emerald-600 text-white' : 'bg-[var(--bg-surface-alt)] text-[var(--text-secondary)] hover:bg-[var(--bg-surface)]'}`}>
                            {f === 'all' ? t('admin.all_routes') : f === 'public' ? t('admin.public_routes') : t('admin.private_routes')}
                          </button>
                        ))}
                      </div>
                    )}
                    <AdminRoutesPanel
                      groupedRoutes={groupedRoutes}
                      expandedRegions={expandedRegions}
                      onToggleRegion={toggleRegion}
                      renderRouteCard={(route) => (
                        <AdminRouteCard
                          key={route.id}
                          route={route}
                          isPendingMode={activeTab === 'pending'}
                          isEditing={editingRouteId === route.id}
                          isDeleting={deletingRouteId === route.id}
                          editFields={editFields}
                          processingId={processingId}
                          difficultyIcons={DIFFICULTY_ICONS}
                          renderRouteMap={renderRouteMap}
                          onEditFieldChange={handleRouteEditFieldChange}
                          onSaveRouteUpdate={saveRouteUpdate}
                          onCancelEdit={() => setEditingRouteId(null)}
                          onApproveRoute={handleRouteApproval}
                          onStartEditRoute={startEditRoute}
                          onRequestDelete={setDeletingRouteId}
                          onConfirmDelete={confirmDeleteRoute}
                          onCancelDelete={() => setDeletingRouteId(null)}
                          onMakePublic={handleMakeRoutePublic}
                        />
                      )}
                    />
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        {!inboxOnly && (
          <div className="px-6 py-3 bg-[var(--bg-input)] border-t border-[var(--border-primary)] flex justify-between items-center">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              <p className="text-2xs font-bold text-[var(--text-muted)] uppercase tracking-widest">{t('admin.footer_label')}</p>
            </div>
            <button onClick={onClose} className="bg-white text-black px-6 py-2.5 rounded-xl font-black text-tiny uppercase tracking-widest active:scale-95 transition-all shadow-xl">{t('admin.close_panel')}</button>
          </div>
        )}
      </div>

      {inboxTester && currentUser && (
        <TesterInboxModal
          currentUser={currentUser}
          isAdmin={true}
          testerUid={inboxTester.uid}
          testerName={inboxTester.displayName || 'Tester'}
          testerEmail={inboxTester.email || ''}
          testerPhotoURL={inboxTester.photoURL}
          onClose={() => setInboxTester(null)}
        />
      )}
      {inboxUser && currentUser && (
        <TesterInboxModal
          currentUser={currentUser}
          isAdmin={true}
          testerUid={inboxUser.uid}
          testerName={inboxUser.displayName || 'Bruker'}
          testerEmail={inboxUser.email || ''}
          testerPhotoURL={inboxUser.photoURL}
          onClose={() => setInboxUser(null)}
        />
      )}
      {showOwnInbox && currentUser && (
        <TesterInboxModal
          currentUser={currentUser}
          isAdmin={false}
          onClose={() => setShowOwnInbox(false)}
        />
      )}
    </div >
  );
};

export default AdminPanel;


